import { create } from "zustand";
import type { Comment } from "../types/document";
import { api } from "../lib/api";

export interface Collaborator {
  id: string | number;
  name: string;
  color: string;
}

export type CommentFilter = "all" | "open" | "resolved";

interface CommentPopoverState {
  position: { x: number; y: number };
  lineStart: number;
  lineEnd: number;
}

interface CollaborationState {
  collaborators: Collaborator[];
  comments: Comment[];
  isConnected: boolean;

  focusedCommentId: string | null;
  commentFilter: CommentFilter;
  commentPopover: CommentPopoverState | null;

  setCollaborators: (collaborators: Collaborator[]) => void;
  fetchComments: (documentId: string) => Promise<void>;
  addComment: (comment: Comment) => void;
  resolveComment: (commentId: string) => void;
  deleteComment: (commentId: string) => void;
  setComments: (comments: Comment[]) => void;
  setIsConnected: (connected: boolean) => void;

  setFocusedComment: (id: string | null) => void;
  setCommentFilter: (filter: CommentFilter) => void;
  openCommentPopover: (state: CommentPopoverState) => void;
  closeCommentPopover: () => void;
}

export const useCollaborationStore = create<CollaborationState>()((set, get) => ({
  collaborators: [],
  comments: [],
  isConnected: false,
  focusedCommentId: null,
  commentFilter: "all",
  commentPopover: null,

  setCollaborators: (collaborators) => set({ collaborators }),

  fetchComments: async (documentId: string) => {
    try {
      const comments = await api.get<Comment[]>(`/api/v1/comments/document/${documentId}`);
      set({ comments });
    } catch {
      // Non-fatal: keep existing comments in state
    }
  },

  addComment: (comment) => {
    set((s) => ({ comments: [...s.comments, comment] }));
    api.post("/api/v1/comments", {
      document_id: comment.document_id,
      content: comment.content,
      line_start: comment.line_start,
      line_end: comment.line_end,
      parent_id: comment.parent_id,
    }).then((saved) => {
      set((s) => ({
        comments: s.comments.map((c) =>
          c.id === comment.id ? { ...c, ...(saved as Comment) } : c,
        ),
      }));
    }).catch(() => {
      // Rollback on failure
      set((s) => ({ comments: s.comments.filter((c) => c.id !== comment.id) }));
    });
  },

  resolveComment: (commentId) => {
    const prev = get().comments.find((c) => c.id === commentId);
    const newResolved = !prev?.resolved;
    set((s) => ({
      comments: s.comments.map((c) =>
        c.id === commentId ? { ...c, resolved: newResolved } : c,
      ),
    }));
    api.put(`/api/v1/comments/${commentId}`, { resolved: newResolved }).catch(() => {
      set((s) => ({
        comments: s.comments.map((c) =>
          c.id === commentId ? { ...c, resolved: !newResolved } : c,
        ),
      }));
    });
  },

  deleteComment: (commentId) => {
    const prev = get().comments;
    set((s) => ({
      comments: s.comments.filter((c) => c.id !== commentId),
      focusedCommentId: s.focusedCommentId === commentId ? null : s.focusedCommentId,
    }));
    api.delete(`/api/v1/comments/${commentId}`).catch(() => {
      set({ comments: prev });
    });
  },

  setComments: (comments) => set({ comments }),
  setIsConnected: (isConnected) => set({ isConnected }),

  setFocusedComment: (id) => set({ focusedCommentId: id }),
  setCommentFilter: (filter) => set({ commentFilter: filter }),
  openCommentPopover: (popover) => set({ commentPopover: popover }),
  closeCommentPopover: () => set({ commentPopover: null }),
}));
